<template lang="pug">
    div(class="_gg", :style="{ width }")
        div(class="_ggfr")
            div(class="_ggcl")
                svg(class="_ggso", :style="transform", viewBox="0 0 64 64")
                    clipPath(:id="'_ggcp' + _uid")
                        path(:d="'M32 0A32 32 0 1 0 32 64A32 32 0 1 0 32 0zM32 ' + thickness + 'A' + (32 - thickness) + ' ' + (32 - thickness) + ' 0 1 1 32 ' + (64 - thickness) + 'A' + (32 - thickness) + ' ' + (32 - thickness) + ' 0 1 1 32 ' + thickness + 'z'")
                    radialGradient(id="_ggrg")
                        stop(offset="75%", stop-opacity="0")
                        stop(offset="100%", stop-opacity="0.05")
                    g(:clip-path="'url(#_ggcp' + _uid + ')'")
                        rect(class="_ggbg", x="0", y="0", width="64", height="64")
                        rect(class="_ggro", x="0", y="32", width="64", height="32", v-if="solid")
                        ellipse(cx="32", cy="32", rx="33", ry="32", fill="url(#_ggrg)")
            svg(v-if="needle", class="_ggnd", :style="transform", viewBox="0 0 64 8")
                path(d="M63.75 7.995L0 4 63.75.005z")
                circle(cx="64", cy="4", r="4")
            svg(v-if="tick", class="_ggtk", viewBox="0 0 64 32", :style="{ opacity: tickOpacity, strokeWidth: tickThickness }")
                path(v-for="t in tick", :d="_d(t)", vector-effect="non-scaling-stroke")
                text(v-for="t in tick", x="32", :y="6 + thickness", :transform="'rotate(' + ((t - min) / (max - min) - .5) * 180 + ' 32 32)'", vector-effect="non-scaling-stroke") {{t}}
        div(v-if="$slots.default || $scopedSlots.default", class="_ggca")
            slot(:value="value")
</template>

<script>
    /**
     * A generic gauge control that displays a needle that rotates to display
     * a value clamped to a given range, and/or a solid color indicator, and
     * an optional caption using a slot. Use foreground color classes to
     * manipulate the bar color.
     */
    export default {
        name: 'ib-gauge-solid',

        props: {
            /**
             * Width of element. Height is always 54% of width plus space for a caption if applicable.
             */
            width: String,
            /**
             * Current value to display; clamped to [min,max] inclusive.
             */
            value: {
                type: Number,
                default: 0
            },
            /**
             * Minimum value that can be displayed.
             */
            min: {
                type: Number,
                default: 0
            },
            /**
             * Maximum value that can be displayed.
             */
            max: {
                type: Number,
                default: 1
            },
            /**
             * Thickness of solid outline, where 10 is default thickness.
             */
            thickness: {
                type: Number,
                default: 10
            },
            /**
             * If true, display needle.
             */
            needle: {
                type: Boolean,
                default: true
            },
            /**
             * If true, display solid indicator using foreground color.
             */
            solid: {
                type: Boolean,
                default: true
            },
            /**
             * Optional array of tick point values.
             */
            tick: Array,
            /**
             * Tick mark opacity.
             */
            tickOpacity: {
                type: Number,
                default: .25
            },
            /**
             * Tick mark thickness.
             */
            tickThickness: {
                type: Number,
                default: 1
            }
        },

        computed: {
            transform() {
                return this.$util.transform({r: Math.max(0, Math.min(1, (this.value - this.min) / (this.max - this.min))) * 180});
            }
        },

        methods: {
            /** @private */
            _d(value) {
                const rad = (value - this.min) / (this.max - this.min) * Math.PI;
                const cos = Math.cos(rad);
                const sin = Math.sin(rad);
                const r = 32 - this.thickness;
                return 'M' + (32 - cos * 32) + ' ' + (32 - sin * 32) + 'L' + (32 - cos * r) + ' ' + (32 - sin * r);
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._gg {
        width: 160px;
        color: @fg-accent;
    }
    ._ggfr {
        position: relative;
        margin-bottom: 4%;
        padding-top: 50%;
    }
    ._ggcl {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }
    ._ggca {
        margin-top: 4px;
        text-align: center;
        color: @fg;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    ._ggbg {
        fill: fade(@fg-max, 8%);
    }

    ._con {
        ._ggnd {
            position: absolute;
            overflow: visible;
            right: 50%;
            bottom: -5.75%;
            width: 48%;
            height: 12%;
            fill: @fg;
            transform-origin: right;
            transition: transform .3s ease;
        }
        ._ggso {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 200%;
            transform-origin: center;
            transition: transform .3s ease;
        }
        ._ggtk {
            position: absolute;
            overflow: visible;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            fill: none;
            stroke: @fg;
            > text {
                fill: @fg;
                stroke: none;
                text-anchor: middle;
                font-size: 5px;
            }
        }
    }
</style>

<docs>
    Examples:
    ```jsx
        new Vue({
            data() { return {
                value1: 30,
                value2: 60,
                value3: 0
            }},
            template:
                `<ib-context>
                    <p class="after-md">Basic example: <ib-field-numeric v-model="value1" width="8ch"/></p>
                    <ib-gauge-solid :value="value1" :min="0" :max="100"/>
                    <p class="before-lg after-md">With caption and color: <ib-field-numeric v-model="value2" width="8ch"/></p>
                    <ib-gauge-solid :value="value2" :min="0" :max="100" class="fg-negative">
                        <template slot-scope="scope">Value: {{scope.value}}</template>
                    </ib-gauge-solid>
                    <p class="before-lg after-md">Tick mark, no needle: <ib-field-numeric v-model="value3" width="8ch" decimal/></p>
                    <ib-gauge-solid :value="value3" :min="-1" :max="1" :needle="false" :tick="[0]" class="fg-warning"/>
                </ib-context>`
        });
    ```
</docs>
